"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, HeartPulse, UserPlus, Activity } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "24-7-care",
  title: "טיפול 24/7",
  description: "השגחה וטיפול צמוד מסביב לשעון, המעניקים שקט נפשי לכם וליקיריכם בכל שעות היממה.",
  icon: Clock,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_care_24_7.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_care_24_7.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_care_24_7.png-wvc-sizes",
    alt: "טיפול סיעודי 24/7"
  }
}, {
  id: "medical-escort",
  title: "ליווי רפואי",
  description: "ליווי אישי ומקצועי לבדיקות, טיפולים רפואיים וסידורים מחוץ לבית, תוך דאגה לכל צורך.",
  icon: Activity,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_medical_escort.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_medical_escort.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_medical_escort.png-wvc-sizes",
    alt: "ליווי רפואי צמוד"
  }
}, {
  id: "daily-assistance",
  title: "עזרה יומיומית",
  description: "סיוע רגיש ומכבד בפעולות היומיום, כולל רחצה, הלבשה, האכלה ושמירה על היגיינה אישית.",
  icon: HeartPulse,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_daily_assistance.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_daily_assistance.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_daily_assistance.png-wvc-sizes",
    alt: "עזרה בפעולות יומיומיות"
  }
}, {
  id: "companionship",
  title: "הפגת בדידות",
  description: "חברה נעימה, שיחה, קריאה משותפת ופעילויות פנאי לשיפור מצב הרוח והאיכות החיים.",
  icon: UserPlus,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_companionship.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_companionship.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/nursing_companionship.png-wvc-sizes",
    alt: "הפגת בדידות וחברה"
  }
}];
export default function NursingServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: actionsRef,
    isVisible: isActionsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="שירותים-סיעודיים" className="py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-default font-bold text-primary mb-4">
            שירותים סיעודיים מותאמים אישית
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            אנו מציעים מגוון רחב של שירותי סיעוד מקצועיים ומסורים, הניתנים על ידי צוות מיומן ואכפתי, במטרה להעניק ליקיריכם את הטיפול הטוב ביותר בסביבה המוכרת והנוחה שלהם.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {services.map((service, i) => {
          const Icon = service.icon;
          return <Card key={service.id} data-index={i} className="overflow-hidden flex flex-col h-full hover:shadow-lg transition-shadow duration-300 border-border/50">
                <div className="relative h-48 w-full overflow-hidden bg-muted">
                  <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} className="object-cover w-full h-full transition-transform duration-500 hover:scale-105" loading="lazy" />
                  <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm p-2 rounded-lg shadow-sm">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xl font-default text-foreground">{service.title}</CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-base font-default text-muted-foreground">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        <div ref={actionsRef} className={`mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ease-out delay-400 ${isActionsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Link to="/NursingCareNowPage" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto text-base font-default">
              הזמן מטפל סיעודי עכשיו
            </Button>
          </Link>
          <Link to="/ContactPage" className="w-full sm:w-auto">
            <Button variant="outline" size="lg" className="w-full sm:w-auto text-base font-default">
              צור קשר לייעוץ חינם
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}